import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import MobileFooter from "./mobileFooter";
import DesktopFooter from "./desktopFooter";
import { addAddonToCartAction, addOnModalVisibility } from "../../../../store/slices/common/addOn";
import { BUY_NOW } from "../../../../constants/common/common";
import { setSelectedCartProduct } from "../../../../store/slices/cartSlice";
import isEmpty from "lodash.isempty";
import PropTypes from "prop-types";
import useAnalytics from "../../../../analytics/useAnalytics";
import { cleverTapCommonEvents } from "../../../../analytics/clevertapConstants";
import { GA4_EVENTS } from "../../../../analytics/gaConstant";
import { ga4addonAdded } from "../../../../analytics/gaEvents";
import { redirectToCheckoutOrLogin } from "../../../../utils/common";

/**
 * Index component displays the footer section for the addon selection.
 *
 * This component shows price details including the base price of the product and the
 * price of selected addons. It also includes a continue button for proceeding with
 * the selected items. Depending on whether the user is on a mobile or desktop device,
 * it renders different layouts.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered footer component.
 */
const Index = ({ commonCleverTapData }) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const selectedAddons = useSelector((state) => state.addOn.selectedAddons);
  const isMobile = useSelector((state) => state?.appConfig?.isMobile);
  const clientCurrentClickedItem = useSelector((state) => state?.pdp?.clientCurrentClickedItem);
  const selectedCartProduct = useSelector((state) => state?.cart?.selectedCartProduct);

  const basePrice = isEmpty(selectedCartProduct)
    ? clientCurrentClickedItem?.productPrice?.price
    : selectedCartProduct?.price;

  const { selectedDate, timeSlots } = useSelector(
    (state) => state.shippingDateAndSlotSelection?.selectedDeliveryDetails,
  );
  const addOnInfo = useSelector((state) => state.addOn?.addOnInfo);
  const currentItem = useSelector((state) => state?.pdp?.clientCurrentClickedItem);
  const continueAddon = useSelector((state) => state?.addOn?.continueAddon);
  const isBuyNowClicked = useSelector((state) => state.pdp?.checkBuyNowOrProceedToPay);
  const checkLoginStatus = useSelector((state) => state.fus.loginDetail?.loggedin);
  const pincode = useSelector((state) => state?.locationLock?.selectedDomesticLocation?.pincode);
  const selectedCurrency = useSelector((state) => state?.currencyList?.userSelectedCurrency?.currency?.id);

  useEffect(() => {
    if (continueAddon && isBuyNowClicked === BUY_NOW) {
      redirectToCheckoutOrLogin({ checkLoginStatus, isMobile, dispatcher: dispatch });
      dispatch(addOnModalVisibility(false));
      dispatch(setSelectedCartProduct({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continueAddon, isBuyNowClicked]);

  /** */
  const calculateAddonTotal = () => {
    return selectedAddons.reduce((total, addon) => total + addon.price * addon.quantity, 0);
  };

  /** */
  const calculateAddonCount = () => {
    return selectedAddons.reduce((count, addon) => count + addon.quantity, 0);
  };
  const addonsTotal = calculateAddonTotal();

  /**
   * Calculates the total price including base price and the selected addons.
   *
   * @returns {number} The total price, which is the sum of the base price and the total
   * cost of selected addons.
   */
  const calculateTotal = () => {
    return basePrice + addonsTotal + (timeSlots?.timeSlots?.shippingPrice || 0);
  };

  const selectedAddonCount = selectedAddons.length;
  const totalPrice = calculateTotal();

  /** */
  const addProducts = () => {
    const formData = new FormData();
    const payload = {
      countryGeoId: currentItem?.countryGeoId === "IND" ? "" : currentItem?.countryGeoId,
      itemDesiredDeliveryDate: selectedDate,
      masterProductId: addOnInfo?.masterProductId,
      orderItemSeqId: "ID" + addOnInfo?.orderItemSeqId,
      pinCode: addOnInfo?.parentItemDetails?.pinCode,
      shippingMethodId: addOnInfo?.parentItemDetails?.shippingMethodId,
      timeSlotId: addOnInfo?.parentItemDetails?.timeSlotId,
    };

    for (const key in payload) {
      formData.append(key, payload[key]);
    }
    selectedAddons.forEach((item) => {
      const itemP = `${item.productId}_${item.quantity}`;
      formData.append("addonProductId", item.isFromCart ? `${itemP}_${item.itemIndex}` : itemP);
    });
    const queryString = new URLSearchParams(formData).toString();

    return queryString;
  };

  /** */
  const onContinueClick = () => {
    const gaProperties = ga4addonAdded(selectedAddons, pincode, checkLoginStatus, "PDP", selectedCurrency);
    dispatch(addAddonToCartAction(addProducts()));
    trackEvent(
      cleverTapCommonEvents.addonContinueClicked,
      {
        ...commonCleverTapData,
        total_addon_quantity: selectedAddonCount,
        total_addon_price: addonsTotal,
        is_addon_added: selectedAddonCount > 0,
        is_date_today: null,
        is_date_tomorrow: null,
        is_variant_selected: null,
      },
      ["clevertap"],
    );
    if (selectedAddonCount > 0) {
      trackEvent(GA4_EVENTS?.ADDONS_ADDED, { ...gaProperties }, ["ga4"]);
    } else {
      trackEvent(GA4_EVENTS?.CONTINUE_WITHOUT_ADDON, {}, ["ga4"]);
    }
  };

  if (isMobile) {
    return (
      <MobileFooter
        totalPrice={totalPrice}
        calculateAddonCount={calculateAddonCount}
        selectedAddonCount={selectedAddonCount}
        onContinueClick={onContinueClick}
      />
    );
  }
  return (
    <DesktopFooter
      calculateAddonCount={calculateAddonCount}
      basePrice={basePrice}
      calculateAddonTotal={calculateAddonTotal}
      calculateTotal={calculateTotal}
      onContinueClick={onContinueClick}
    />
  );
};

Index.propTypes = {
  commonCleverTapData: PropTypes.object,
};
export default Index;
